<template>
  <div class="arrival-plan-form" ref="madeForm">
    <div class="form-section">
      <div class="section-title">铁路作业票</div>
      <div class="ticket-row">
        <el-upload
          v-if="!isDetails()"
          action=""
          accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          :http-request="handleTicketUpload"
          :before-upload="beforeTicketUpload"
          :show-file-list="false"
          :disabled="uploading || loading"
        >
          <el-button type="primary" size="small" icon="el-icon-upload" :loading="uploading">上传铁路作业票</el-button>
        </el-upload>
        <div v-if="formData.railwayOperationTicketUrl" class="ticket-card">
          <i class="el-icon-paperclip ticket-icon"></i>
          <span class="ticket-name">{{ ticketDisplayName }}</span>
          <el-button type="text" size="small" @click="openTicket">查看</el-button>
        </div>
        <div v-else class="ticket-empty">未上传</div>
      </div>
    </div>

    <div class="form-section">
      <div class="section-title">计划信息</div>
      <el-form ref="form" :model="formData" :rules="rules" label-width="120px" size="small" :disabled="isDetails() || loading">
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="计划号" prop="planNo">
              <el-input v-model="formData.planNo" disabled placeholder="保存后生成" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="客户" prop="custNo">
              <el-select
                v-model="formData.custNo"
                filterable
                remote
                clearable
                reserve-keyword
                :remote-method="loadCustomerOptions"
                :loading="customerLoading"
                placeholder="请选择客户"
                @visible-change="handleCustomerVisible"
                @change="handleCustomerChange"
              >
                <el-option
                  v-for="item in customerOptions"
                  :key="String(item.value)"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="合同" prop="contractName">
              <el-input v-model="formData.contractName" readonly placeholder="请选择合同" @focus="openContractModal" @click.native="openContractModal">
                <el-button v-if="!isDetails()" slot="append" icon="el-icon-search" @click.stop="openContractModal"></el-button>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="合同编号" prop="contractNo">
              <el-input v-model="formData.contractNo" readonly placeholder="选择合同后带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="合同煤种" prop="cargoName">
              <el-input v-model="formData.cargoName" readonly placeholder="选择合同后带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="车次编号" prop="trainNumber">
              <el-input v-model.trim="formData.trainNumber" maxlength="50" show-word-limit placeholder="请输入车次编号" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="预计到达时间" prop="trainArrivalTime">
              <el-date-picker
                v-model="formData.trainArrivalTime"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                placeholder="请选择预计到达时间"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="到达作业方式" prop="arrivalOperationMode">
              <el-select v-model="formData.arrivalOperationMode" placeholder="请选择到达作业方式" @change="handleArrivalOperationModeChange">
                <el-option label="翻车机卸车" :value="ARRIVAL_OPERATION_DUMP" />
                <el-option label="汽运短倒" :value="ARRIVAL_OPERATION_SHORT_TRANSFER" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="到达点" prop="cargoFlow">
              <el-select v-model="formData.cargoFlow" placeholder="请选择到达点" @change="handleCargoFlowChange">
                <el-option label="铁运入库" :value="CARGO_FLOW_IN_STOCK" />
                <el-option label="铁运至电厂" :value="CARGO_FLOW_TO_POWER_PLANT" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="isInStockFlow(formData.cargoFlow)" :span="8">
            <el-form-item label="库位" prop="locationMeta">
              <el-select
                v-model="formData.locationMeta"
                filterable
                clearable
                :loading="locationsLoading"
                :disabled="locationsLoading || (!locationOptions.length && !formData.locationMeta)"
                :placeholder="locationsLoading ? '库位加载中' : '请选择合同签署库位'"
                @change="handleLocationChange"
              >
                <el-option
                  v-for="item in locationOptions"
                  :key="item.key"
                  :label="item.value"
                  :value="item.key"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="发站" prop="beginStation">
              <el-input v-model.trim="formData.beginStation" maxlength="50" placeholder="PDF解析后自动带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="到站" prop="endStation">
              <el-input v-model.trim="formData.endStation" maxlength="50" placeholder="PDF解析后自动带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="发货人" prop="shipperName">
              <el-input v-model.trim="formData.shipperName" maxlength="100" placeholder="PDF解析后自动带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="收货单位" prop="receiverName">
              <el-input v-model.trim="formData.receiverName" maxlength="100" placeholder="PDF解析后自动带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="货权人" prop="cargoOwnerName">
              <el-input v-model.trim="formData.cargoOwnerName" maxlength="100" placeholder="备注有货权人时自动带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="承运人" prop="carrierName">
              <el-input v-model.trim="formData.carrierName" maxlength="100" placeholder="PDF解析后自动带出" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="是否有空箱" prop="hasEmptyContainer">
              <el-select v-model="formData.hasEmptyContainer" placeholder="请选择" @change="handleHasEmptyContainerChange">
                <el-option label="否" :value="NO" />
                <el-option label="是" :value="YES" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="Number(formData.hasEmptyContainer) === YES" :span="8">
            <el-form-item label="空箱数" prop="emptyContainerCount">
              <el-input-number v-model="formData.emptyContainerCount" :min="0" :max="9999" :precision="0" controls-position="right" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注" prop="remark">
              <el-input v-model="formData.remark" type="textarea" maxlength="200" show-word-limit :rows="2" placeholder="请输入备注" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="carriage-section">
      <div class="section-header">
        <div class="section-title">车节明细 <span class="section-summary">车厢数：{{ totalInfo.carriageCount }}，净重：{{ totalInfo.totalWeightText }} 吨</span></div>
        <div v-if="!isDetails()" class="section-actions">
          <el-button size="mini" icon="el-icon-upload2" :loading="mineExcelLoading" @click="triggerMineExcelSelect">上传矿发Excel</el-button>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="addCarriage">新增</el-button>
          <input ref="mineExcelInput" class="hidden-file-input" type="file" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" @change="handleMineExcelFileChange" />
        </div>
      </div>
      <div class="carriage-table-wrap">
        <table class="carriage-table">
          <thead>
            <tr>
              <th class="index-col">序号</th>
              <th>车型</th>
              <th>车号</th>
              <th>箱型</th>
              <th>箱号</th>
              <th>重量</th>
              <th>净重</th>
              <th class="dump-col">可翻卸</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="formData.list && formData.list.length">
              <template v-for="(row, rowIndex) in formData.list">
                <tr v-for="(container, containerIndex) in displayContainerRows(row)" :key="carriageRowKey(row, rowIndex, container, containerIndex)">
                  <td v-if="containerIndex === 0" :rowspan="displayContainerRows(row).length" class="index-cell">
                    <div>{{ rowIndex + 1 }}</div>
                    <template v-if="!isDetails()">
                      <el-button type="text" size="mini" @click="removeCarriage(rowIndex)">删除</el-button>
                      <el-button type="text" size="mini" @click="addContainerRow(row)">加箱</el-button>
                    </template>
                    <div v-if="row.remark" class="row-remark">需补录</div>
                  </td>
                  <td v-if="containerIndex === 0" :rowspan="displayContainerRows(row).length">
                    <el-input v-if="!isDetails()" v-model.trim="row.wagonType" size="mini" maxlength="30" placeholder="车型" @input="handleCarriageBasisChange(row)" />
                    <span v-else>{{ row.wagonType }}</span>
                  </td>
                  <td v-if="containerIndex === 0" :rowspan="displayContainerRows(row).length">
                    <el-input v-if="!isDetails()" v-model.trim="row.carriageNo" size="mini" maxlength="30" placeholder="车号" />
                    <span v-else>{{ row.carriageNo }}</span>
                  </td>
                  <td>
                    <el-input v-if="!isDetails()" v-model.trim="container.containerType" size="mini" maxlength="60" placeholder="箱型" @input="handleContainerBasisChange(row)" />
                    <span v-else>{{ container.containerType }}</span>
                  </td>
                  <td>
                    <el-input v-if="!isDetails()" v-model.trim="container.containerNo" size="mini" maxlength="60" placeholder="箱号" />
                    <span v-else>{{ container.containerNo }}</span>
                  </td>
                  <td>
                    <el-input v-if="!isDetails()" v-model="container.containerTotalWeight" size="mini" placeholder="重量" />
                    <span v-else>{{ formatWeight(container.containerTotalWeight || container.cargoNetWeight || row.weight || row.netWeight) }}</span>
                  </td>
                  <td v-if="containerIndex === 0" :rowspan="displayContainerRows(row).length">
                    <el-input v-if="!isDetails()" v-model="row.netWeight" size="mini" placeholder="净重" @input="updateCarriageWeight(row, 'netWeight')" />
                    <span v-else>{{ formatWeight(row.netWeight || row.weight) }}</span>
                  </td>
                  <td v-if="containerIndex === 0" :rowspan="displayContainerRows(row).length" :class="String(row.canDump) === '1' ? 'dump-ok' : 'dump-no'">
                    {{ String(row.canDump) === '1' ? '是' : '否' }}
                  </td>
                </tr>
              </template>
            </template>
            <tr v-else>
              <td colspan="8" class="empty-cell">请上传铁路作业票、矿发Excel或手动添加车节</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <span slot="dialog_footer" class="dialog-footer" v-if="!isDetails()">
      <el-button icon="el-icon-circle-plus-outline" type="primary" @click="submitForm" :loading="loading">提交</el-button>
      <el-button icon="el-icon-circle-close" @click="$emit('handleClose')" :disabled="loading">取消</el-button>
    </span>

    <SelectPlan ref="selectPlan" @replace="replace"></SelectPlan>
    <el-dialog title="矿发Excel字段匹配" :visible.sync="manualMappingVisible" width="520px" append-to-body>
      <div class="mapping-tip">矿发Excel字段未自动匹配成功，请手动选择对应字段。</div>
      <el-form label-width="90px" size="small">
        <el-form-item label="车型字段" required>
          <el-select v-model="mappingValues.wagonType" placeholder="请选择车型字段" filterable>
            <el-option v-for="item in excelHeaders" :key="'wagon-' + item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="车号字段" required>
          <el-select v-model="mappingValues.carriageNo" placeholder="请选择车号字段" filterable>
            <el-option v-for="item in excelHeaders" :key="'carriage-' + item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="箱型字段">
          <el-select v-model="mappingValues.containerType" placeholder="请选择箱型字段" filterable clearable>
            <el-option v-for="item in excelHeaders" :key="'type-' + item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="箱号字段">
          <el-select v-model="mappingValues.containerNo" placeholder="请选择箱号字段" filterable clearable>
            <el-option v-for="item in excelHeaders" :key="'no-' + item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="manualMappingVisible = false">取消</el-button>
        <el-button type="primary" :loading="mineExcelLoading" @click="submitManualMapping">确认匹配</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  add,
  update,
  getDetail,
  getCustomerOptions,
  uploadRailwayTicket,
  importCarriageFile,
  importMineExcelPreview,
  matchMineExcel,
  getDumpSupportedDict,
  getContractLocations,
} from '../api';
import SelectPlan from './select-plan.vue';

const CARGO_FLOW_IN_STOCK = 1
const CARGO_FLOW_TO_POWER_PLANT = 2
const ARRIVAL_OPERATION_DUMP = 1
const ARRIVAL_OPERATION_SHORT_TRANSFER = 2
const YES = 1
const NO = 0
const RAILWAY_TARE_WEIGHT = 22
const MAX_WEIGHT = 99999
const MAX_CARRIAGE_COUNT = 99999
const EXCEL_MIME_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

function numberValue (value) {
  if (value === undefined || value === null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function formatWeightValue (value) {
  const num = numberValue(value)
  if (num === null) return ''
  return Number(num.toFixed(3)).toString()
}

function calcGrossWeight (netWeight, tareWeight) {
  const net = numberValue(netWeight)
  const tare = numberValue(tareWeight === '' || tareWeight === undefined ? RAILWAY_TARE_WEIGHT : tareWeight)
  if (net === null || tare === null) return ''
  return formatWeightValue(net + tare)
}

function val (row, keys, fallback) {
  for (let i = 0; i < keys.length; i++) {
    const value = row && row[keys[i]]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return fallback
}

function normalizeDumpValue (value) {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/’/g, "'")
    .replace(/\s/g, '')
    .replace(/英尺/g, 'FT')
    .replace(/集装箱/g, '箱')
  if (normalized.indexOf('20') >= 0 && normalized.indexOf('35') >= 0 && normalized.indexOf('敞顶箱') >= 0) {
    return '20FT_35T_OPEN_TOP'
  }
  return normalized
}

function normalizeDumpDictRows (rows = []) {
  const supported = []
  const fields = ['dictKey', 'dictValue', 'key', 'value']
  const dictRows = Array.isArray(rows) ? rows : []
  dictRows.forEach((row = {}) => {
    fields.forEach(field => {
      const normalized = normalizeDumpValue(row[field])
      if (normalized && supported.indexOf(normalized) < 0) supported.push(normalized)
    })
  })
  return supported
}

function isDumpSupported (value, supportedValues = []) {
  const normalized = normalizeDumpValue(value)
  if (!normalized) return false
  return supportedValues.indexOf(normalized) >= 0
}

function splitLines (value) {
  if (Array.isArray(value)) return value
  return String(value || '').split(/[\n,，;；/、]+/).map(item => item.trim()).filter(Boolean)
}

function joinContainerField (list = [], field) {
  return list.map(item => item && item[field]).filter(item => item !== undefined && item !== null && item !== '').join('\n')
}

function normalizeContainerList (row = {}) {
  const source = Array.isArray(row.containerList) ? row.containerList : []
  if (source.length) {
    return source.map((item, index) => ({
      ...item,
      sortNo: item.sortNo || index + 1,
      containerNo: item.containerNo || '',
      containerType: item.containerType || '',
      containerTareWeight: item.containerTareWeight,
      cargoNetWeight: item.cargoNetWeight,
      containerTotalWeight: item.containerTotalWeight,
    }))
  }
  const containerNos = splitLines(val(row, ['containerNoText', 'containerNo', 'containerNos'], ''))
  const containerTypes = splitLines(val(row, ['containerTypeText', 'containerType'], ''))
  const tareWeights = splitLines(val(row, ['containerTareWeightText', 'containerTareWeight'], ''))
  const totalWeights = splitLines(val(row, ['containerTotalWeightText', 'containerTotalWeight'], ''))
  const maxLength = Math.max(containerNos.length, containerTypes.length, tareWeights.length, totalWeights.length)
  const list = []
  for (let i = 0; i < maxLength; i++) {
    list.push({
      sortNo: i + 1,
      containerNo: containerNos[i] || '',
      containerType: containerTypes[i] || containerTypes[0] || '',
      containerTareWeight: tareWeights[i] || '',
      containerTotalWeight: totalWeights[i] || '',
    })
  }
  return list.filter(item => item.containerNo || item.containerType || item.containerTareWeight || item.containerTotalWeight)
}

function effectiveContainerList (row = {}) {
  return normalizeContainerList(row).filter(item => (
    item.containerNo ||
    item.containerType ||
    item.containerTareWeight ||
    item.containerTotalWeight ||
    item.cargoNetWeight
  ))
}

function calcCanDump (row = {}, supportedValues = []) {
  if (supportedValues.length) {
    if (isDumpSupported(row.wagonType, supportedValues)) return '1'
    const containers = normalizeContainerList(row)
    return containers.some(item => isDumpSupported(item.containerType, supportedValues)) ? '1' : '0'
  }
  return String(row.canDump || '0') === '1' ? '1' : '0'
}

function hasDumpCheckBasis (row = {}) {
  if (String(row.wagonType || '').trim()) return true
  return normalizeContainerList(row).some(item => String(item.containerType || '').trim())
}

function isUnsupportedDumpCarriage (row = {}) {
  return hasDumpCheckBasis(row) && String(row.canDump) !== '1'
}

function normalizeCarriage (row = {}, index = 0, supportedValues = []) {
  const containerList = normalizeContainerList(row)
  const displayList = containerList.length ? containerList : [{ sortNo: 1 }]
  const tareWeight = val(row, ['tareWeight'], RAILWAY_TARE_WEIGHT)
  const netWeight = val(row, ['netWeight', 'weight'], '')
  const grossWeight = val(row, ['grossWeight'], calcGrossWeight(netWeight, tareWeight))
  return {
    key: row.key || row.id || `${Date.now()}-${index}`,
    id: row.id,
    wagonType: val(row, ['wagonType', 'carKind', 'vehicleType'], ''),
    carriageNo: val(row, ['carriageNo', 'carNo', 'carriageNumber'], ''),
    hasContainer: row.hasContainer !== undefined && row.hasContainer !== null ? Number(row.hasContainer) : (containerList.length ? YES : NO),
    containerList: displayList,
    containerTypeText: joinContainerField(containerList, 'containerType'),
    containerNoText: joinContainerField(containerList, 'containerNo'),
    containerTareWeightText: joinContainerField(containerList, 'containerTareWeight'),
    containerTotalWeightText: joinContainerField(containerList, 'containerTotalWeight'),
    tareWeight: tareWeight === undefined || tareWeight === null || tareWeight === '' ? RAILWAY_TARE_WEIGHT : tareWeight,
    grossWeight,
    netWeight,
    weight: netWeight,
    canDump: calcCanDump({ ...row, containerList }, supportedValues),
    dumpCheckMessage: row.dumpCheckMessage,
    remark: row.remark || '',
  }
}

function normalizeResponseData (res) {
  const body = res && res.data
  if (!body) return {}
  if (body.data !== undefined) return body.data
  return body
}

function normalizeRows (res) {
  const data = normalizeResponseData(res)
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.records)) return data.records
  if (data && Array.isArray(data.data)) return data.data
  return []
}

function isSuccessResponse (res) {
  const body = res && res.data
  return !!(body && (body.success || body.code === 200))
}

function parseMaybeJson (value) {
  if (!value) return {}
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (e) {
    return {}
  }
}

function normalizeLocation (row = {}) {
  return {
    storeSubId: row.storeSubId || row.id || row.storeSubID,
    stockCode: row.stockCode,
    stockName: row.stockName,
    locationCode: row.locationCode,
    locationName: row.locationName,
  }
}

function locationLabel (row = {}) {
  const stock = row.stockName || row.stockCode || ''
  const location = row.locationName || row.locationCode || ''
  return [stock, location].filter(Boolean).join(' / ') || '未命名库位'
}

function makeLocationOption (row = {}) {
  const item = normalizeLocation(row)
  return {
    key: JSON.stringify(item),
    value: locationLabel(item),
    ...item,
  }
}

function isTicketFile (file) {
  const name = (file && file.name) || ''
  const type = (file && file.type) || ''
  return type === 'application/pdf' ||
    EXCEL_MIME_TYPES.indexOf(type) > -1 ||
    /\.(pdf|xls|xlsx)$/i.test(name)
}

export default {
  components: { SelectPlan },
  props: {
    transmitData: {
      required: true,
      type: Object,
      default: () => ({})
    },
  },
  data () {
    return {
      CARGO_FLOW_IN_STOCK,
      CARGO_FLOW_TO_POWER_PLANT,
      ARRIVAL_OPERATION_DUMP,
      ARRIVAL_OPERATION_SHORT_TRANSFER,
      YES,
      NO,
      loading: false,
      uploading: false,
      mineExcelLoading: false,
      locationsLoading: false,
      locationsLoaded: false,
      locationOptions: [],
      customerLoading: false,
      customerOptions: [],
      dumpSupportedValues: [],
      dumpDictReady: false,
      mineExcelFile: null,
      excelHeaders: [],
      mappingValues: {},
      manualMappingVisible: false,
      formData: this.makeDefaultForm(),
      rules: {
        custNo: [{ required: true, message: '请选择客户', trigger: 'change' }],
        contractName: [{ required: true, message: '请选择合同', trigger: 'change' }],
        contractNo: [{ required: true, message: '请选择合同', trigger: 'change' }],
        cargoName: [{ required: true, message: '请选择合同煤种', trigger: 'change' }],
        trainNumber: [{ required: true, message: '请输入车次编号', trigger: 'blur' }],
        trainArrivalTime: [{ required: true, message: '请选择预计到达时间', trigger: 'change' }],
        arrivalOperationMode: [{ required: true, message: '请选择到达作业方式', trigger: 'change' }],
        cargoFlow: [{ required: true, message: '请选择到达点', trigger: 'change' }],
        locationMeta: [{ required: true, message: '请选择库位', trigger: 'change' }],
        emptyContainerCount: [{ required: true, message: '请输入空箱数', trigger: 'blur' }],
      },
    }
  },
  computed: {
    ticketDisplayName () {
      return this.formData.railwayOperationTicketOriginalName ||
        this.formData.railwayOperationTicketName ||
        '铁路作业票.pdf'
    },
    totalInfo () {
      const list = Array.isArray(this.formData.list) ? this.formData.list : []
      const totalWeight = list.reduce((sum, row) => sum + (numberValue(row.netWeight || row.weight) || 0), 0)
      return {
        carriageCount: list.length,
        totalWeight,
        totalWeightText: totalWeight.toFixed(3),
      }
    },
  },
  created () {
    this.loadDumpSupportedDict()
    this.loadCustomerOptions('')
    const { type, id } = this.transmitData
    if (type !== 'add' && id) {
      this.loadDetail(id)
    }
  },
  methods: {
    makeDefaultForm () {
      return {
        cargoFlow: CARGO_FLOW_IN_STOCK,
        arrivalOperationMode: ARRIVAL_OPERATION_DUMP,
        hasEmptyContainer: NO,
        emptyContainerCount: 0,
        list: [],
      }
    },
    isDetails () {
      return this.transmitData.type === 'view'
    },
    isInStockFlow (value) {
      return Number(value || CARGO_FLOW_IN_STOCK) === CARGO_FLOW_IN_STOCK
    },
    openTicket () {
      if (this.formData.railwayOperationTicketUrl) {
        window.open(this.formData.railwayOperationTicketUrl, '_blank')
      }
    },
    formatWeight (value) {
      const num = numberValue(value)
      return num === null ? '' : num.toFixed(2)
    },
    displayContainerRows (row = {}) {
      const list = normalizeContainerList(row)
      return list.length ? list : [{ sortNo: 1 }]
    },
    carriageRowKey (row, rowIndex, container, containerIndex) {
      return `${row.key || row.id || row.carriageNo || rowIndex}-${container.id || container.containerNo || container.sortNo || containerIndex}`
    },
    loadDumpSupportedDict () {
      getDumpSupportedDict().then(res => {
        this.dumpSupportedValues = normalizeDumpDictRows(normalizeRows(res))
        this.dumpDictReady = true
        this.recalculateDumpState()
      }).catch(() => {
        this.dumpSupportedValues = []
        this.dumpDictReady = false
      })
    },
    recalculateDumpState () {
      const list = Array.isArray(this.formData.list) ? this.formData.list : []
      list.forEach((row, index) => {
        const next = normalizeCarriage(row, index, this.dumpSupportedValues)
        Object.keys(next).forEach(key => {
          this.$set(row, key, next[key])
        })
      })
      this.enforceShortTransferIfNeeded(false)
    },
    hasUnsupportedDumpCarriage () {
      return (this.formData.list || []).some(isUnsupportedDumpCarriage)
    },
    enforceShortTransferIfNeeded (showMessage = true) {
      if (!this.dumpDictReady) return
      if (Number(this.formData.arrivalOperationMode || ARRIVAL_OPERATION_DUMP) === ARRIVAL_OPERATION_DUMP && this.hasUnsupportedDumpCarriage()) {
        this.$set(this.formData, 'arrivalOperationMode', ARRIVAL_OPERATION_SHORT_TRANSFER)
        if (showMessage) this.$message.warning('存在不可翻卸车节，到达作业方式已切换为汽运短倒')
      }
    },
    loadCustomerOptions (query) {
      this.customerLoading = true
      getCustomerOptions({
        key: query || '',
        deptName: query || '',
      }).then(res => {
        const rows = normalizeRows(res)
        const options = rows.map(row => ({
          label: row.deptName || row.custName || row.name || row.label,
          value: row.id || row.custNo || row.value,
        })).filter(row => row.label && row.value !== undefined && row.value !== null)
        const current = this.formData.custNo && this.formData.custName
          ? { label: this.formData.custName, value: this.formData.custNo }
          : null
        const hasCurrent = current && options.some(item => String(item.value) === String(current.value))
        this.customerOptions = hasCurrent || !current ? options : [current, ...options]
      }).finally(() => {
        this.customerLoading = false
      })
    },
    handleCustomerVisible (visible) {
      if (visible && !this.customerOptions.length) {
        this.loadCustomerOptions('')
      }
    },
    handleCustomerChange (value) {
      const selected = this.customerOptions.find(item => String(item.value) === String(value))
      this.$set(this.formData, 'custName', selected ? selected.label : '')
      this.clearContract()
    },
    clearContract () {
      const contractKeys = ['contractExtId', 'contractNo', 'contractName', 'cargoNo', 'cargoName']
      contractKeys.forEach(key => {
        this.$set(this.formData, key, '')
      })
      this.clearLocation()
    },
    clearLocation () {
      this.locationOptions = []
      this.locationsLoaded = false
      const locationKeys = ['locationMeta', 'storeSubId', 'stockCode', 'stockName', 'locationCode', 'locationName']
      locationKeys.forEach(key => {
        this.$set(this.formData, key, key === 'storeSubId' ? null : '')
      })
    },
    openContractModal () {
      if (this.isDetails()) return
      if (!this.formData.custNo) {
        this.$message.warning('请先选择客户')
        return
      }
      this.$refs.selectPlan.open({
        custNo: this.formData.custNo,
      })
    },
    replace (row) {
      const fields = ['contractNo', 'contractName', 'cargoNo', 'cargoName', 'custNo', 'custName', 'trainNumber']
      fields.forEach(key => {
        if (row[key] !== undefined && row[key] !== null) {
          this.$set(this.formData, key, row[key])
        }
      })
      this.$set(this.formData, 'contractExtId', row.id || row.contractExtId)
      this.ensureCustomerOption()
      this.clearLocation()
      if (this.isInStockFlow(this.formData.cargoFlow)) {
        this.loadContractLocations()
      }
    },
    ensureCustomerOption () {
      if (!this.formData.custNo || !this.formData.custName) return
      const exists = this.customerOptions.some(item => String(item.value) === String(this.formData.custNo))
      if (!exists) {
        this.customerOptions = [{ label: this.formData.custName, value: this.formData.custNo }, ...this.customerOptions]
      }
    },
    loadContractLocations (fallbackData = {}) {
      if (!this.formData.custNo || !(this.formData.contractExtId || this.formData.contractNo) || !this.formData.cargoNo) {
        this.clearLocation()
        return Promise.resolve([])
      }
      this.locationsLoading = true
      this.locationsLoaded = false
      return getContractLocations({
        custNo: this.formData.custNo,
        contractExtId: this.formData.contractExtId,
        contractNo: this.formData.contractNo,
        cargoNo: this.formData.cargoNo,
      }).then(res => {
        const options = normalizeRows(res).map(makeLocationOption)
        const fallbackOption = this.makeFallbackLocationOption(fallbackData)
        const merged = fallbackOption && !this.findLocationKey(options, fallbackData)
          ? [fallbackOption, ...options]
          : options
        this.locationOptions = merged
        this.locationsLoaded = true
        const selectedKey = this.findLocationKey(merged, fallbackData)
        if (selectedKey) {
          this.$set(this.formData, 'locationMeta', selectedKey)
          this.handleLocationChange(selectedKey)
        } else if (merged.length === 1) {
          this.$set(this.formData, 'locationMeta', merged[0].key)
          this.handleLocationChange(merged[0].key)
        } else if (!merged.length && this.isInStockFlow(this.formData.cargoFlow)) {
          this.$message.warning('当前合同未签署库位，禁止提交')
        }
        return merged
      }).catch(() => {
        this.locationOptions = []
        this.locationsLoaded = true
        this.$message.error('合同库位加载失败')
        return []
      }).finally(() => {
        this.locationsLoading = false
      })
    },
    makeFallbackLocationOption (data = {}) {
      if (!(data.storeSubId || data.stockCode || data.stockName || data.locationCode || data.locationName)) return null
      return makeLocationOption(data)
    },
    findLocationKey (options, data = {}) {
      const match = (options || []).find(option => {
        const meta = parseMaybeJson(option.key)
        return (data.storeSubId && String(meta.storeSubId) === String(data.storeSubId)) ||
          (data.locationCode && meta.locationCode === data.locationCode) ||
          (data.locationName && meta.locationName === data.locationName)
      })
      return match && match.key
    },
    handleCargoFlowChange (value) {
      this.$set(this.formData, 'cargoFlow', Number(value))
      if (!this.isInStockFlow(value)) {
        this.clearLocation()
        return
      }
      if (this.formData.contractExtId || this.formData.contractNo) {
        this.loadContractLocations(this.formData)
      }
    },
    handleArrivalOperationModeChange (value) {
      if (this.dumpDictReady && Number(value) === ARRIVAL_OPERATION_DUMP && this.hasUnsupportedDumpCarriage()) {
        this.$set(this.formData, 'arrivalOperationMode', ARRIVAL_OPERATION_SHORT_TRANSFER)
        this.$message.warning('存在不可翻卸车节，到达作业方式必须为汽运短倒')
      }
    },
    handleHasEmptyContainerChange (value) {
      if (Number(value) !== YES) {
        this.$set(this.formData, 'emptyContainerCount', 0)
      }
    },
    handleLocationChange (value) {
      const location = parseMaybeJson(value)
      const locationKeys = ['storeSubId', 'stockCode', 'stockName', 'locationCode', 'locationName']
      locationKeys.forEach(key => {
        this.$set(this.formData, key, location[key] || (key === 'storeSubId' ? null : ''))
      })
    },
    beforeTicketUpload (file) {
      if (!isTicketFile(file)) {
        this.$message.warning('请上传PDF或Excel格式的铁路作业票')
        return false
      }
      const sizeOk = file.size / 1024 / 1024 < 20
      if (!sizeOk) this.$message.warning('铁路作业票文件不能超过20MB')
      return sizeOk
    },
    async handleTicketUpload ({ file, onSuccess, onError }) {
      this.uploading = true
      try {
        const uploadResp = await uploadRailwayTicket(file)
        if (!isSuccessResponse(uploadResp)) {
          this.$message.error((uploadResp.data && uploadResp.data.msg) || '铁路作业票上传失败')
          if (onError) onError(uploadResp)
          return
        }
        const data = parseMaybeJson(normalizeResponseData(uploadResp))
        this.$set(this.formData, 'railwayOperationTicketUrl', data.link || data.url || data.fileUrl || data.path)
        this.$set(this.formData, 'railwayOperationTicketName', data.name || data.fileName || file.name)
        this.$set(this.formData, 'railwayOperationTicketOriginalName', data.originalName || data.originalFileName || file.name)
        this.$message.success('铁路作业票上传成功')
        if (onSuccess) onSuccess(uploadResp, file)
        try {
          const parseResp = await importCarriageFile(file)
          if (isSuccessResponse(parseResp)) {
            this.applyImportedData(normalizeResponseData(parseResp))
          } else {
            this.$message.warning((parseResp.data && parseResp.data.msg) || '文件解析失败，请手动补录')
          }
        } catch (e) {
          this.$message.warning('文件解析失败，请手动补录')
        }
      } catch (e) {
        this.$message.error('铁路作业票上传失败')
        if (onError) onError(e)
      } finally {
        this.uploading = false
      }
    },
    applyImportedData (data = {}) {
      const rows = Array.isArray(data.list) ? data.list : []
      if (data.trainNumber) this.$set(this.formData, 'trainNumber', data.trainNumber)
      if (data.trainArrivalTime || data.arrivalTime) {
        this.$set(this.formData, 'trainArrivalTime', data.trainArrivalTime || data.arrivalTime)
      }
      if (data.beginStation) this.$set(this.formData, 'beginStation', data.beginStation)
      if (data.endStation) this.$set(this.formData, 'endStation', data.endStation)
      if (data.shipperName) this.$set(this.formData, 'shipperName', data.shipperName)
      if (data.receiverName) this.$set(this.formData, 'receiverName', data.receiverName)
      if (data.cargoOwnerName) this.$set(this.formData, 'cargoOwnerName', data.cargoOwnerName)
      if (data.carrierName) this.$set(this.formData, 'carrierName', data.carrierName)
      if (rows.length) {
        this.$set(this.formData, 'list', rows.map((row, index) => normalizeCarriage(row, index, this.dumpSupportedValues)))
        this.syncTotals()
      }
      const manualCount = (this.formData.list || []).filter(row => row.remark).length
      if (manualCount) this.$message.warning(manualCount + ' 条车节需手动补录')
      if (data.needMineExcel) {
        const warning = Array.isArray(data.parseWarnings) && data.parseWarnings.length
          ? data.parseWarnings[0]
          : '未解析到车型、车号'
        this.showNeedMineExcelTip(warning + '，请上传矿发Excel表格；若不上传，可在车节信息中手动录入。')
      }
      this.enforceShortTransferIfNeeded(false)
      this.$message.success(rows.length ? '已导入 ' + rows.length + ' 条车节' : '未解析到车节数据')
    },
    showNeedMineExcelTip (message) {
      this.$confirm(message, '提示', {
        confirmButtonText: '上传矿发Excel',
        cancelButtonText: '手动录入',
        type: 'warning',
      }).then(() => {
        this.triggerMineExcelSelect()
      }).catch(() => {})
    },
    beforeMineExcelUpload (file) {
      const name = (file && file.name) || ''
      const type = (file && file.type) || ''
      const ok = EXCEL_MIME_TYPES.indexOf(type) > -1 || /\.(xls|xlsx)$/i.test(name)
      if (!ok) {
        this.$message.warning('请上传矿发Excel表格')
        return false
      }
      const sizeOk = file.size / 1024 / 1024 < 20
      if (!sizeOk) this.$message.warning('矿发Excel不能超过20MB')
      return sizeOk
    },
    triggerMineExcelSelect () {
      if (this.mineExcelLoading || this.isDetails()) return
      if (this.$refs.mineExcelInput) {
        this.$refs.mineExcelInput.click()
      }
    },
    handleMineExcelFileChange (event) {
      const input = event.target
      const file = input.files && input.files[0]
      input.value = ''
      if (file) this.handleMineExcelUpload(file)
    },
    getCurrentRowsJson () {
      const rows = (this.formData.list || []).map(item => ({
        wagonType: item.wagonType,
        carriageNo: item.carriageNo,
        netWeight: item.netWeight,
        weight: item.weight || item.netWeight,
        containerList: effectiveContainerList(item),
      }))
      return JSON.stringify(rows)
    },
    async handleMineExcelUpload (file) {
      if (!this.beforeMineExcelUpload(file)) return
      this.mineExcelFile = file
      this.mineExcelLoading = true
      try {
        const res = await importMineExcelPreview(file, this.getCurrentRowsJson())
        if (isSuccessResponse(res)) {
          this.applyMineExcelResult(normalizeResponseData(res))
        } else {
          this.$message.warning((res.data && res.data.msg) || '矿发Excel解析失败，请手动录入')
        }
      } catch (e) {
        this.$message.warning('矿发Excel解析失败，请手动录入')
      } finally {
        this.mineExcelLoading = false
      }
    },
    applyMineExcelResult (data = {}) {
      const headers = Array.isArray(data.excelHeaders) ? data.excelHeaders : []
      this.excelHeaders = headers
      const fields = ['trainNumber', 'trainArrivalTime', 'beginStation', 'endStation', 'shipperName', 'receiverName', 'cargoOwnerName', 'carrierName']
      fields.forEach(key => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          this.$set(this.formData, key, data[key])
        }
      })
      const rows = Array.isArray(data.list) ? data.list.map((row, index) => normalizeCarriage(row, index, this.dumpSupportedValues)) : []
      if (rows.length) {
        this.$set(this.formData, 'list', rows)
        this.syncTotals()
        this.enforceShortTransferIfNeeded(false)
      }
      if (data.needManualMapping) {
        this.mappingValues = {}
        this.manualMappingVisible = true
        this.$message.warning('矿发Excel字段未匹配成功，请手动选择字段')
        return
      }
      this.$message.success(rows.length ? '已匹配 ' + rows.length + ' 条车节信息' : '矿发Excel未匹配到车节信息')
    },
    async submitManualMapping () {
      if (!this.mineExcelFile) {
        this.$message.warning('请重新上传矿发Excel表格')
        return
      }
      if (!this.mappingValues.wagonType || !this.mappingValues.carriageNo) {
        this.$message.warning('请选择车型和车号字段')
        return
      }
      this.mineExcelLoading = true
      try {
        const res = await matchMineExcel(this.mineExcelFile, this.mappingValues, this.getCurrentRowsJson())
        if (isSuccessResponse(res)) {
          this.manualMappingVisible = false
          this.applyMineExcelResult(normalizeResponseData(res))
        } else {
          this.$message.warning((res.data && res.data.msg) || '字段匹配失败，请手动录入')
        }
      } catch (e) {
        this.$message.warning('字段匹配失败，请手动录入')
      } finally {
        this.mineExcelLoading = false
      }
    },
    addCarriage () {
      this.formData.list.push(normalizeCarriage({ tareWeight: RAILWAY_TARE_WEIGHT }, this.formData.list.length, this.dumpSupportedValues))
      this.syncTotals()
    },
    addContainerRow (row) {
      const list = normalizeContainerList(row)
      list.push({ sortNo: list.length + 1 })
      this.$set(row, 'containerList', list)
      this.$set(row, 'hasContainer', list.length ? YES : NO)
      this.handleCarriageBasisChange(row)
    },
    removeCarriage (index) {
      this.formData.list.splice(index, 1)
      this.syncTotals()
    },
    handleCarriageBasisChange (row) {
      this.$set(row, 'canDump', calcCanDump(row, this.dumpSupportedValues))
      this.enforceShortTransferIfNeeded()
    },
    handleContainerBasisChange (row) {
      this.$set(row, 'containerList', normalizeContainerList(row))
      this.$set(row, 'hasContainer', effectiveContainerList(row).length ? YES : NO)
      this.handleCarriageBasisChange(row)
    },
    updateCarriageWeight (row, field) {
      if (field === 'netWeight') {
        if (row.tareWeight === '' || row.tareWeight === undefined || row.tareWeight === null) {
          this.$set(row, 'tareWeight', RAILWAY_TARE_WEIGHT)
        }
        this.$set(row, 'grossWeight', calcGrossWeight(row.netWeight, row.tareWeight))
        this.$set(row, 'weight', row.netWeight)
      }
      if (field === 'tareWeight' && row.netWeight !== '' && row.netWeight !== undefined && row.netWeight !== null) {
        this.$set(row, 'grossWeight', calcGrossWeight(row.netWeight, row.tareWeight))
      }
      if (field === 'grossWeight' || field === 'tareWeight') {
        const gross = numberValue(row.grossWeight)
        const tare = numberValue(row.tareWeight)
        if (gross !== null && tare !== null) {
          this.$set(row, 'netWeight', formatWeightValue(gross - tare))
          this.$set(row, 'weight', row.netWeight)
        }
      }
      this.syncTotals()
    },
    syncTotals () {
      this.$set(this.formData, 'carriageCount', this.totalInfo.carriageCount)
      this.$set(this.formData, 'totalWeight', this.totalInfo.totalWeight)
    },
    canUseCarriage (row = {}) {
      const net = numberValue(row.netWeight || row.weight)
      return !!row.carriageNo && !!row.wagonType && net > 0
    },
    hasUsableCarriageRows () {
      return (this.formData.list || []).some(row => this.canUseCarriage(row))
    },
    validateBusinessRules () {
      if (!this.formData.railwayOperationTicketUrl && !this.hasUsableCarriageRows()) {
        this.$message.error('请上传铁路作业票或矿发Excel，并解析出有效车节明细')
        return false
      }
      if (!this.formData.contractNo && !this.formData.contractExtId) {
        this.$message.error('请选择合同')
        return false
      }
      if (!this.formData.cargoNo && !this.formData.cargoName) {
        this.$message.error('请选择合同煤种')
        return false
      }
      if (this.isInStockFlow(this.formData.cargoFlow) && this.locationsLoading) {
        this.$message.warning('合同库位加载中，请稍后提交')
        return false
      }
      if (this.isInStockFlow(this.formData.cargoFlow) && this.locationsLoaded && !this.locationOptions.length) {
        this.$message.error('当前合同未签署库位，禁止提交')
        return false
      }
      if (this.isInStockFlow(this.formData.cargoFlow) && !this.formData.locationCode && !this.formData.locationName) {
        this.$message.error('铁运入库必须选择库位')
        return false
      }
      const list = this.formData.list || []
      if (!list.length) {
        this.$message.error('车节明细不能为空，请导入或手动添加')
        return false
      }
      if (list.length > MAX_CARRIAGE_COUNT) {
        this.$message.error('车厢数不能超过' + MAX_CARRIAGE_COUNT)
        return false
      }
      for (let i = 0; i < list.length; i++) {
        const row = list[i] || {}
        const prefix = '第' + (i + 1) + '行'
        if (!this.canUseCarriage(row)) {
          this.$message.error(prefix + '缺少车型、车号或净重')
          return false
        }
        const net = numberValue(row.netWeight || row.weight)
        if (net > MAX_WEIGHT) {
          this.$message.error(prefix + '重量不能超过' + MAX_WEIGHT + '吨')
          return false
        }
      }
      if (Number(this.formData.hasEmptyContainer || NO) === YES && Number(this.formData.emptyContainerCount || 0) <= 0) {
        this.$message.error('有空箱时请录入空箱数')
        return false
      }
      const unsupportedIndex = this.dumpDictReady ? list.findIndex(item => isUnsupportedDumpCarriage(item)) : -1
      if (unsupportedIndex >= 0 && Number(this.formData.arrivalOperationMode || ARRIVAL_OPERATION_DUMP) !== ARRIVAL_OPERATION_SHORT_TRANSFER) {
        this.$message.error('第' + (unsupportedIndex + 1) + '行车节不可翻卸，到达作业方式必须为汽运短倒')
        this.$set(this.formData, 'arrivalOperationMode', ARRIVAL_OPERATION_SHORT_TRANSFER)
        return false
      }
      for (let i = 0; i < list.length; i++) {
        const row = list[i] || {}
        const containers = effectiveContainerList(row)
        for (let j = 0; j < containers.length; j++) {
          const containerWeight = numberValue(containers[j].containerTotalWeight)
          if (containerWeight !== null && containerWeight > MAX_WEIGHT) {
            this.$message.error('第' + (i + 1) + '行第' + (j + 1) + '个箱重量不能超过' + MAX_WEIGHT + '吨')
            return false
          }
        }
      }
      return true
    },
    buildSubmitData () {
      this.syncTotals()
      const list = (this.formData.list || []).map(item => ({
        id: item.id,
        wagonType: String(item.wagonType || '').trim(),
        carriageNo: String(item.carriageNo || '').trim(),
        tareWeight: RAILWAY_TARE_WEIGHT,
        grossWeight: calcGrossWeight(item.netWeight || item.weight, RAILWAY_TARE_WEIGHT),
        netWeight: item.netWeight || item.weight,
        weight: item.netWeight || item.weight,
        hasContainer: effectiveContainerList(item).length ? YES : NO,
        containerList: effectiveContainerList(item),
        canDump: calcCanDump(item, this.dumpSupportedValues) === '1' ? YES : NO,
        dumpCheckMessage: item.dumpCheckMessage,
        remark: item.remark,
      }))
      const submitData = {
        ...this.formData,
        cargoFlow: Number(this.formData.cargoFlow || CARGO_FLOW_IN_STOCK),
        arrivalOperationMode: Number(this.formData.arrivalOperationMode || ARRIVAL_OPERATION_DUMP),
        hasEmptyContainer: Number(this.formData.hasEmptyContainer || NO),
        emptyContainerCount: Number(this.formData.hasEmptyContainer) === YES ? Number(this.formData.emptyContainerCount || 0) : 0,
        list,
      }
      delete submitData.locationMeta
      if (!this.isInStockFlow(submitData.cargoFlow)) {
        submitData.storeSubId = undefined
        submitData.stockCode = ''
        submitData.stockName = ''
        submitData.locationCode = ''
        submitData.locationName = ''
      }
      return submitData
    },
    submitForm () {
      this.$refs.form.validate(valid => {
        if (!valid || !this.validateBusinessRules()) {
          this.$message.error('错误, 请验证必填项并录入符合规则的数据')
          return
        }
        const submitData = this.buildSubmitData()
        const submitMethod = this.transmitData.type === 'add' ? add : update
        this.loading = true
        submitMethod(submitData).then(({ data }) => {
          this.$emit('handleClose', 'refresh')
          this.$message({
            type: 'success',
            message: data.msg || '操作成功',
          })
        }).finally(() => {
          this.loading = false
        })
      })
    },
    loadDetail (id) {
      this.loading = true
      getDetail(id).then(res => {
        const detail = normalizeResponseData(res) || {}
        const next = {
          ...this.makeDefaultForm(),
          ...detail,
          cargoFlow: Number(detail.cargoFlow || CARGO_FLOW_IN_STOCK),
          arrivalOperationMode: Number(detail.arrivalOperationMode || ARRIVAL_OPERATION_DUMP),
          hasEmptyContainer: Number(detail.hasEmptyContainer || NO),
          emptyContainerCount: Number(detail.emptyContainerCount || 0),
          list: Array.isArray(detail.list) ? detail.list.map((row, index) => normalizeCarriage(row, index, this.dumpSupportedValues)) : [],
        }
        this.formData = next
        this.ensureCustomerOption()
        const fallbackOption = this.makeFallbackLocationOption(detail)
        if (fallbackOption) {
          this.locationOptions = [fallbackOption]
          this.$set(this.formData, 'locationMeta', fallbackOption.key)
        }
        if (this.isInStockFlow(this.formData.cargoFlow) && (this.formData.contractExtId || this.formData.contractNo)) {
          this.loadContractLocations(detail)
        }
        this.syncTotals()
      }).finally(() => {
        this.loading = false
      })
    },
  },
}
</script>

<style scoped lang="scss">
.arrival-plan-form {
  padding-bottom: 8px;
}

.form-section,
.carriage-section {
  margin-bottom: 12px;
}

.section-title {
  margin-bottom: 8px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-summary {
  margin-left: 8px;
  color: #606266;
  font-weight: 400;
}

.ticket-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
}

.ticket-card {
  display: inline-flex;
  align-items: center;
  max-width: 520px;
  color: #606266;
}

.ticket-icon {
  margin-right: 6px;
  color: #409eff;
}

.ticket-name {
  max-width: 390px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-empty {
  color: #909399;
}

.arrival-plan-form ::v-deep .el-select,
.arrival-plan-form ::v-deep .el-date-editor {
  width: 100%;
}

.arrival-plan-form ::v-deep .el-table th.el-table__cell {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.hidden-file-input {
  display: none;
}

.carriage-table-wrap {
  width: 100%;
  max-height: 360px;
  overflow: auto;
  border: 1px solid #ebeef5;
}

.carriage-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  table-layout: fixed;
  background: #fff;
}

.carriage-table th,
.carriage-table td {
  border: 1px solid #ebeef5;
  padding: 8px;
  text-align: center;
  vertical-align: middle;
  color: #606266;
  font-size: 13px;
  word-break: break-all;
}

.carriage-table th {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.index-col {
  width: 90px;
}

.dump-col {
  width: 90px;
}

.index-cell {
  color: #303133;
  font-weight: 600;
}

.row-remark {
  margin-top: 4px;
  color: #e6a23c;
  font-size: 12px;
  font-weight: 400;
}

.empty-cell {
  height: 72px;
  color: #909399;
}

.dump-ok {
  color: #67c23a;
  font-weight: 600;
}

.dump-no {
  color: #f56c6c;
  font-weight: 600;
}

.mapping-tip {
  margin-bottom: 12px;
  color: #606266;
}

.mapping-tip + .el-form ::v-deep .el-select {
  width: 100%;
}
</style>
